import { Card, Col, Container, Row, Badge } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { cents, showMoney  } from "./Data/Money";
import { useLoaderData, useNavigate     } from "react-router-dom";
import { PageTitle } from "./Controls/PageTitle";
import { facilityImg } from "./Data/Common";
import { SubListEntry } from "./Data/ApiTypes";
import { pluralize } from "./Data/English";
import { showSubscriptionStatus } from "./Data/Subscription";
import { useAtomValue } from "jotai";
import { actingAsAtom, authAtom } from "./Data/Atoms";
import { SubscriptionStatusPill } from "./Controls/SubscriptionStatusPill";
import { CarFrontFill } from "react-bootstrap-icons";

const STATUS_GROUP_ORDER = ['Active', 'Pending', 'Cancelled', 'Inactive', 'Future', 'Waitlisted', 'Revoked', 'Unknown Status'] as const;

function groupSubsByStatus(subs: SubListEntry[]): Map<string, SubListEntry[]> {
    const map = new Map<string, SubListEntry[]>();
    for (const sub of subs) {
        const label = showSubscriptionStatus(sub.status, sub.start);
        const list = map.get(label) ?? [];
        list.push(sub);
        map.set(label, list);
    }
    return map;
}

export function UserPlansPage() {
    const nav       = useNavigate();
    const auth      = useAtomValue( authAtom );
    const actingAs  = useAtomValue( actingAsAtom );
    const subs      = useLoaderData() as SubListEntry[];
    const effectiveParkerId = actingAs?.parkerId ?? auth.account?.parkerId;
    const byStatus = groupSubsByStatus(subs);
    const orderedLabels = [...STATUS_GROUP_ORDER.filter(l => byStatus.has(l)), ...[...byStatus.keys()].filter(l => !STATUS_GROUP_ORDER.includes(l as typeof STATUS_GROUP_ORDER[number]))];

    return <Container fluid>
        <Row>
            <Col>
                <PageTitle className="mb-1">
                    Plans
                </PageTitle>
                {subs.length === 0 && (
                    <div className="d-flex flex-column align-items-center text-center py-5 gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: 64, height: 64, background: "rgba(214,31,132,0.1)" }}>
                            <CarFrontFill style={{ fontSize: 28, color: "#D61F84" }} />
                        </div>
                        <div>
                            <div className="fw-semibold mb-1">No parking plans yet</div>
                            <div className="text-muted small">Find a monthly parking spot near you.</div>
                        </div>
                        <Button onClick={() => nav( "/" )}>
                            Search for Monthly Parking
                        </Button>
                    </div>
                )}
                {orderedLabels.map(statusLabel => {
                    const groupSubs = byStatus.get(statusLabel) ?? [];
                    if (groupSubs.length === 0) return null;
                    return (
                        <div key={statusLabel} className="mb-4">
                            {statusLabel !== 'Active' && (
                                <div className="text-muted small text-uppercase mb-2">{statusLabel}</div>
                            )}
                            {groupSubs.map( sub => {
                    const facility = sub.facility;
                    const quantity = sub.quantity;
                    const price    = sub.cachedMrr > 0 ? sub.cachedMrr : sub.price;
                    const notMine  = sub.parkerId != effectiveParkerId;
                    const spots    = sub.quantity;

                    return <Card key={sub.subscriptionId} className="mb-3" style={{ cursor: "pointer" }}
                                 onClick={ () => {
                                    if( notMine ) {
                                        nav( `/user/plan/${sub.subscriptionId}/guest` );
                                        return;
                                    }
                                    nav( `/user/plan/${sub.subscriptionId}` );
                                 }}>
                        <Card.Header className="text-start d-flex align-items-center justify-content-between gap-2 py-1">
                            <span className="d-flex align-items-center gap-1">
                                {notMine && <Badge className="bg-primary">Guest</Badge>}
                                <SubscriptionStatusPill status={sub.status} startDate={sub.start} />
                            </span>
                            <span className="text-muted small text-end">{sub.plan.name}</span>
                        </Card.Header>
                        <Card.Body className="purchase-layout gap-2">
                            <div className="grid-area-a">
                                <img className="facility-result-pic"
                                    src={facilityImg( facility.listImageId )}
                                    style={{ aspectRatio: "1 / 1", objectFit: "cover" }} />
                            </div>
                            <div className="grid-area-b d-flex flex-column justify-content-center my-2">
                                <div className="fw-bold">
                                    {sub.facility.name}
                                </div>
                                {facility.address && (
                                    <div className="text-muted small">
                                        {facility.address.city}{facility.address.city && facility.address.state ? ", " : ""}{facility.address.state}
                                    </div>
                                )}
                            </div>
                            <div className="grid-area-c" />
                        </Card.Body>
                        <Card.Footer className="d-flex align-items-center justify-content-between gap-2 flex-wrap py-1 bg-white">
                            { sub.isUsageBasedBilling ? (
                                <span className="small">
                                    <span className="fw-semibold text-body">Usage Based Billing</span>
                                    <span className="text-muted"> — You will be billed by the lot based on your usage</span>
                                </span>
                            ) : (
                                <>
                                    <span className="text-muted small">
                                        {spots} {pluralize( quantity, "Spot", "Spots" )}
                                        {!notMine && sub.unitPrice != null && (
                                            <span className="ms-1">
                                                x {showMoney( cents( sub.unitPrice ), true )}/spot
                                            </span>
                                        )}
                                    </span>
                                    <span className="d-flex align-items-baseline">
                                        <span className="fw-bold">{price != null ? showMoney( cents( price ), true ) : "—"}</span>
                                        <span className="text-muted small">/mo</span>
                                    </span>
                                </>
                            ) }
                        </Card.Footer>
                    </Card>;
                })}
                        </div>
                    );
                })}
            </Col>
        </Row>
    </Container>;
}